import { useCallback, useEffect, useState } from "react";
import Segmented from "@/components/Segmented";
import { Loader2, Download, Check, X, PlugZap, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi, opsDownload } from "./lib/api";

/** FIN-7 — one closeout checklist per project, two owner groups. Everything with a source
 *  record is computed; only the things nothing else knows about are ticked by a person. */
export default function CloseoutPage() {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("oldest");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try { setData(await opsApi.closeoutDashboard(sort)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load closeout"); }
  }, [sort]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading closeout…
    </div>;
  }

  return (
    <div className="page-body space-y-5" data-testid="closeout-page">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[["Jobs in closeout", `${data.summary.projects}`, "closeout-kpi-projects"],
          ["Ready to close", `${data.summary.ready}`, "closeout-kpi-ready"],
          ["Still blocked", `${data.summary.not_ready}`, "closeout-kpi-blocked"],
          ["Marked inactive", `${data.summary.inactive}`, "closeout-kpi-inactive"]]
          .map(([l, v, tid]) => (
          <div key={l} className="rounded-lg border border-slate-200 bg-white p-4"
               data-testid={tid}>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{l}</p>
            <p className="stat-num text-2xl font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Segmented
          options={data.sorts.map((o) => ({ key: o.key, label: o.label,
                                            testId: `closeout-sort-${o.key}` }))}
          value={sort}
          onChange={setSort}
        />
        <Button variant="outline" size="sm" className="ml-auto" data-testid="closeout-export"
                onClick={() => opsDownload("/closeout/export", "pending-closeout.csv")
                  .catch(() => toast.error("That export could not be built"))}>
          <Download className="mr-1.5 h-3.5 w-3.5" /> Export
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white"
           data-testid="closeout-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Job</th>
              <th className="px-3 py-2 text-left">PM</th>
              <th className="px-3 py-2 text-left">Completed</th>
              <th className="px-3 py-2 text-center">Billing</th>
              <th className="px-3 py-2 text-center">AR</th>
              <th className="px-3 py-2 text-center">Retention</th>
              <th className="px-3 py-2 text-center">POs</th>
              <th className="px-3 py-2 text-center">COs</th>
              <th className="px-3 py-2 text-right">Open</th>
              <th className="px-3 py-2 text-right">Closeout</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((r) => (
              <tr key={r.project_id} onClick={() => setOpen(r.project_id)}
                  data-testid={`closeout-row-${r.job_number || r.project_id}`}
                  className="cursor-pointer hover:bg-slate-50">
                <td className="px-3 py-2 text-xs">
                  <span className="font-mono text-slate-500">{r.job_number || "—"}</span>
                  <p className="max-w-[14rem] truncate font-medium text-slate-900">
                    {r.project_name}
                  </p>
                  <p className="max-w-[22rem] truncate text-[11px] text-slate-400">
                    {r.exception_line}
                  </p>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {(r.pm_email || "").split("@")[0] || "—"}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.completion_date || "—"}</td>
                {["billing_complete", "ar_complete", "retention_complete", "pos_closed",
                  "cos_closed"].map((k) => (
                  <td key={k} className="px-3 py-2 text-center">
                    {r[k] ? <Check className="mx-auto h-3.5 w-3.5 text-emerald-600" />
                      : <X className="mx-auto h-3.5 w-3.5 text-rose-400" />}
                  </td>
                ))}
                <td className="px-3 py-2 text-right text-xs">{r.open_issues}</td>
                <td className="px-3 py-2 text-right text-xs font-semibold">
                  {Math.round(r.closeout_pct * 100)}%
                  {r.ready && (
                    <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] text-emerald-700">
                      ready
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-500">
                Nothing in closeout yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <CloseoutDrawer id={open} onClose={() => setOpen(null)} onSaved={load} />}
    </div>
  );
}

function CloseoutDrawer({ id, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [only, setOnly] = useState(true);

  const load = useCallback(async () => {
    try { setD(await opsApi.projectCloseout(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load that checklist"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const tick = async (key, done) => {
    setBusy(true);
    try {
      const note = done ? (window.prompt("Anything worth recording?") ?? "") : "";
      setD(await opsApi.closeoutItem(id, key, { done, note }));
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be recorded"); }
    finally { setBusy(false); }
  };

  const close = async (action) => {
    setBusy(true);
    try {
      const override = !d.ready;
      const reason = override
        ? window.prompt("This job is not ready. Why are you closing it anyway?") : "";
      if (override && !reason) { setBusy(false); return; }
      setD(await opsApi.closeoutClose(id, { action, override, reason: reason || "" }));
      toast.success(action === "close" ? "Job closed" : "Marked inactive");
      onSaved?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : det?.detail || "That could not be done");
    } finally { setBusy(false); }
  };

  const group = (rows, title) => (
    <div className="rounded-lg border border-slate-200">
      <p className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </p>
      {(only ? rows.filter((r) => !r.done) : rows).map((r) => (
        <div key={r.key} className="flex items-start gap-2 px-3 py-1.5 text-xs"
             data-testid={`closeout-item-${r.key}`}>
          {r.done ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
            : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" />}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-800">{r.label}</p>
            <p className="text-[11px] text-slate-500">{r.done ? r.detail : r.blocker}</p>
          </div>
          {r.computed ? (
            <Lock className="mt-0.5 h-3 w-3 shrink-0 text-slate-300" title="calculated" />
          ) : (
            <button onClick={() => tick(r.key, !r.done)} disabled={busy}
                    data-testid={`closeout-tick-${r.key}`}
                    className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-600 hover:border-slate-400">
              {r.done ? "undo" : "confirm"}
            </button>
          )}
        </div>
      ))}
      {only && rows.every((r) => r.done) && (
        <p className="px-3 py-2 text-[11px] text-emerald-700">Everything here is satisfied.</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={onClose}>
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="closeout-drawer">
        {!d ? (
          <div className="flex items-center gap-2 px-6 py-16 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="sticky top-0 flex items-start gap-3 border-b border-slate-200 bg-white px-6 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  {d.job_number} · {Math.round(d.closeout_pct * 100)}% closed
                </p>
                <h2 className="text-lg font-semibold text-slate-900"
                    data-testid="closeout-drawer-title">{d.project_name}</h2>
                <p className={`text-xs font-medium ${d.ready ? "text-emerald-700"
                  : "text-rose-700"}`} data-testid="closeout-drawer-status">{d.status}</p>
              </div>
              <button onClick={onClose} data-testid="closeout-drawer-close"
                      className="ml-auto rounded-md p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={only} data-testid="closeout-exceptions-only"
                       onChange={(e) => setOnly(e.target.checked)} />
                Show only what is blocking it
              </label>
              {group(d.accounting, "Accounting")}
              {group(d.operations, "Operations")}
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => close("close")}
                        data-testid="closeout-close">Close job</Button>
                <Button size="sm" variant="outline" disabled={busy}
                        onClick={() => close("inactivate")}
                        data-testid="closeout-inactivate">Mark inactive</Button>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                   data-testid="closeout-not-wired">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-900">
                  <PlugZap className="h-3 w-3" /> Not connected yet
                </p>
                {d.not_wired.map((n, i) => (
                  <p key={i} className="text-[11px] text-amber-900">· {n}</p>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
